/**
 * TaskFilterFields
 *
 * Controlled task filter inputs shared by the owner form (frozen base filters,
 * search hidden) and the public viewer narrowing panel (search shown).
 */
import { memo, useMemo } from 'react';
import { Stack, TextInput } from '@mantine/core';
import { IconFlag, IconListCheck, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CollapsingMultiSelect } from '@/components/ui/CollapsingMultiSelect';
import { ICON_SIZE_SM } from '@/constants/ui';
import type { ShareViewerFilters } from '@/types/share';

import {
  SHARE_TASK_STATUS_VALUES,
  SHARE_TASK_PRIORITY_VALUES,
} from '../shareConstants';
import { restrictedOptions } from './shareFilterOptions';
import { ShareFilterDateRange } from './ShareFilterDateRange';

interface TaskFilterFieldsProps {
  value: ShareViewerFilters;
  onPatch: (patch: Partial<ShareViewerFilters>) => void;
  showSearch?: boolean;
  /** Owner-shared status subset; empty / missing shows all statuses. */
  allowedStatuses?: string[];
  /** Owner-shared priority subset; empty / missing shows all priorities. */
  allowedPriorities?: string[];
}

export const TaskFilterFields = memo(function TaskFilterFields({
  value,
  onPatch,
  showSearch = false,
  allowedStatuses,
  allowedPriorities,
}: TaskFilterFieldsProps) {
  const { t } = useTranslation();

  const statusOptions = useMemo(
    () =>
      restrictedOptions(
        SHARE_TASK_STATUS_VALUES,
        allowedStatuses,
        'taskStatus',
        t
      ),
    [t, allowedStatuses]
  );
  const priorityOptions = useMemo(
    () =>
      restrictedOptions(
        SHARE_TASK_PRIORITY_VALUES,
        allowedPriorities,
        'priority',
        t
      ),
    [t, allowedPriorities]
  );

  return (
    <Stack gap="xs">
      <CollapsingMultiSelect
        label={t('share.filters.status')}
        placeholder={t('filters.statusPlaceholder')}
        leftSection={<IconListCheck size={ICON_SIZE_SM} />}
        data={statusOptions}
        value={value.taskStatuses ?? []}
        onChange={(next) => onPatch({ taskStatuses: next })}
        clearable
      />
      <CollapsingMultiSelect
        label={t('share.filters.priority')}
        placeholder={t('filters.priorityPlaceholder')}
        leftSection={<IconFlag size={ICON_SIZE_SM} />}
        data={priorityOptions}
        value={value.taskPriorities ?? []}
        onChange={(next) => onPatch({ taskPriorities: next })}
        clearable
      />
      <ShareFilterDateRange
        fromLabel={t('share.filters.dueDateFrom')}
        toLabel={t('share.filters.dueDateTo')}
        fromValue={value.taskDueDateFrom}
        toValue={value.taskDueDateTo}
        onFromChange={(iso) => onPatch({ taskDueDateFrom: iso })}
        onToChange={(iso) => onPatch({ taskDueDateTo: iso })}
      />
      {showSearch && (
        <TextInput
          label={t('share.filters.search')}
          leftSection={<IconSearch size={ICON_SIZE_SM} />}
          value={value.taskSearch ?? ''}
          onChange={(event) =>
            onPatch({ taskSearch: event.currentTarget.value })
          }
        />
      )}
    </Stack>
  );
});
